import {
  QualityRepository,
  CreateQualityInput,
  UpdateQualityInput,
} from '../../domain/repositories/quality.repository';
import { QualityEntity, QualityProps } from '../../domain/entities/quality.entity';
import { IHttpClient } from '../../application/interfaces/http-client.interface';

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
}

export class HttpQualityRepository implements QualityRepository {
  constructor(private readonly httpClient: IHttpClient) {}

  public async getQualities(options?: {
    activeOnly?: boolean;
    search?: string;
  }): Promise<QualityEntity[]> {
    const endpoint = options?.activeOnly ? '/qualities/public' : '/qualities';
    const params = new URLSearchParams();

    if (options?.search?.trim()) {
      params.append('search', options.search.trim());
    }

    const query = params.toString() ? `?${params.toString()}` : '';
    const res = await this.httpClient.get<ApiResponse<QualityProps[]>>(`${endpoint}${query}`);

    if (!res.success || !res.data) {
      return [];
    }

    return res.data.map((item) => new QualityEntity(item));
  }

  public async getQualityById(id: string): Promise<QualityEntity | null> {
    const res = await this.httpClient.get<ApiResponse<QualityProps>>(`/qualities/${encodeURIComponent(id)}`);
    if (!res.success || !res.data) {
      return null;
    }
    return new QualityEntity(res.data);
  }

  public async createQuality(data: CreateQualityInput): Promise<QualityEntity> {
    const res = await this.httpClient.post<ApiResponse<QualityProps>>('/qualities', data);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al crear la cualidad');
    }
    return new QualityEntity(res.data);
  }

  public async updateQuality(id: string, data: UpdateQualityInput): Promise<QualityEntity> {
    const res = await this.httpClient.put<ApiResponse<QualityProps>>(`/qualities/${encodeURIComponent(id)}`, data);
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al actualizar la cualidad');
    }
    return new QualityEntity(res.data);
  }

  public async toggleQualityStatus(id: string, isActive: boolean): Promise<QualityEntity> {
    const res = await this.httpClient.patch<ApiResponse<QualityProps>>(
      `/qualities/${encodeURIComponent(id)}/status`,
      { isActive }
    );
    if (!res.success || !res.data) {
      throw new Error(res.message || 'Error al alternar estado de la cualidad');
    }
    return new QualityEntity(res.data);
  }

  public async deleteQuality(id: string): Promise<void> {
    const res = await this.httpClient.delete<ApiResponse<null>>(`/qualities/${encodeURIComponent(id)}`);
    if (!res.success) {
      throw new Error(res.message || 'Error al eliminar la cualidad');
    }
  }
}
